import { minsToMs } from '@mi-front/core';
import moment from 'moment-timezone';
import { useCallback } from 'react';
import { useCursor, useFocus, useSetFocus } from '../../stores/stream-screen.store';
import { useStreamSelection } from '../../stores/useStreamSelection';
import { useStreamSegmentsRange } from '../segments/useStreamSegmentsQuery';

/**
 * Hook to move focus by an offset
 */
export function useMoveFocus() {
  const streamRange = useStreamSegmentsRange();
  const focus = useFocus();
  const setFocus = useSetFocus();

  return useCallback(
    (offset = 0) => {
      const { from: rangeFrom, to: rangeTo } = streamRange;
      let { from, to } = focus;

      from = moment(from).add(offset).valueOf();
      to = moment(to).add(offset).valueOf();

      if (from < rangeFrom || to > rangeTo) {
        return;
      }

      setFocus({ from, to });
    },
    [streamRange, focus, setFocus],
  );
}

/**
 * Hook to zoom in focus
 */
export function useZoomInFocus() {
  const streamRange = useStreamSegmentsRange();
  const focus = useFocus();
  const setFocus = useSetFocus();

  return useCallback(
    (offset = 0) => {
      const { from: rangeFrom, to: rangeTo } = streamRange;
      let { from, to } = focus;

      from = moment(from).add(offset).valueOf();
      to = moment(to).subtract(offset).valueOf();

      if (from <= rangeFrom || to > rangeTo || from >= to) {
        return;
      }

      setFocus({ from, to });
    },
    [streamRange, focus, setFocus],
  );
}

/**
 * Hook to zoom out focus
 */
export function useZoomOutFocus() {
  const streamRange = useStreamSegmentsRange();
  const focus = useFocus();
  const setFocus = useSetFocus();

  return useCallback(
    (offset = 0) => {
      const { from: rangeFrom, to: rangeTo } = streamRange;
      let { from, to } = focus;

      from = moment(from).subtract(offset).valueOf();
      to = moment(to).add(offset).valueOf();

      if (from < rangeFrom || to > rangeTo) {
        return;
      }

      setFocus({ from, to });
    },
    [streamRange, focus, setFocus],
  );
}

/**
 * Hook to fully zoom in focus to selection or cursor
 */
export function useFullZoomInFocus() {
  const streamRange = useStreamSegmentsRange();
  const cursorTs = useCursor();
  const { selectionRange, selectionExists } = useStreamSelection();
  const setFocus = useSetFocus();

  return useCallback(() => {
    const { from: rangeFrom, to: rangeTo } = streamRange;

    if (!selectionExists) {
      return;
    }

    if (selectionRange.from && selectionRange.to) {
      const newFrom = moment(selectionRange.from).subtract(minsToMs(1)).valueOf();
      const from = newFrom < rangeFrom ? rangeFrom : newFrom;
      const newTo = moment(selectionRange.to).add(minsToMs(1)).valueOf();
      const to = newTo > rangeTo ? rangeTo : newTo;

      setFocus({ from, to });
      return;
    }

    if (cursorTs) {
      const newFrom = moment(cursorTs).subtract(minsToMs(1)).valueOf();
      const from = newFrom < rangeFrom ? rangeFrom : newFrom;
      const newTo = moment(cursorTs).add(minsToMs(1)).valueOf();
      const to = newTo > rangeTo ? rangeTo : newTo;

      setFocus({ from, to });
    }
  }, [streamRange, cursorTs, selectionRange, selectionExists, setFocus]);
}

/**
 * Hook to fully zoom out focus to full stream range
 */
export function useFullZoomOutFocus() {
  const streamRange = useStreamSegmentsRange();
  const setFocus = useSetFocus();

  return useCallback(() => {
    const { from, to } = streamRange;
    setFocus({
      from: moment(from).valueOf(),
      to: moment(to).valueOf(),
    });
  }, [streamRange, setFocus]);
}

/**
 * Hook to zoom in focus to selection start
 */
export function useZoomInSelectionStartFocus() {
  const streamRange = useStreamSegmentsRange();
  const { selectionRange, selectionExists } = useStreamSelection();
  const setFocus = useSetFocus();

  return useCallback(() => {
    const { from: rangeFrom, to: rangeTo } = streamRange;

    if (!selectionExists) {
      return;
    }

    if (selectionRange.from) {
      const newFrom = moment(selectionRange.from).subtract(minsToMs(1)).valueOf();
      const from = newFrom < rangeFrom ? rangeFrom : newFrom;
      const newTo = moment(selectionRange.from).add(minsToMs(1)).valueOf();
      const to = newTo > rangeTo ? rangeTo : newTo;

      setFocus({ from, to });
    }
  }, [streamRange, selectionRange, selectionExists, setFocus]);
}

/**
 * Hook to zoom in focus to selection end
 */
export function useZoomInSelectionEndFocus() {
  const streamRange = useStreamSegmentsRange();
  const { selectionRange, selectionExists } = useStreamSelection();
  const setFocus = useSetFocus();

  return useCallback(() => {
    const { from: rangeFrom, to: rangeTo } = streamRange;

    if (!selectionExists) {
      return;
    }

    if (selectionRange.to) {
      const newFrom = moment(selectionRange.to).subtract(minsToMs(1)).valueOf();
      const from = newFrom < rangeFrom ? rangeFrom : newFrom;
      const newTo = moment(selectionRange.to).add(minsToMs(1)).valueOf();
      const to = newTo > rangeTo ? rangeTo : newTo;

      setFocus({ from, to });
    }
  }, [streamRange, selectionRange, selectionExists, setFocus]);
}
